import React, { useState ,useEffect ,useMemo} from "react";

import { Box, Typography, useTheme, Paper, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../../mech/casting/ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { CorrugatedQuestions } from "./questions";
import { CorrugatedPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "consumables", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics", "packaging"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  consumables: "Consumables",
  // packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions = [],
  productData,
  perlColors,
  perlTypography,
  formulaResult = null,
}) {
  // Use the passed colors and typography directly, with fallbacks
  const colors = perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
    textSecondary: "#A0A0A0",
    surface: "#242526",
    border: "rgba(255, 255, 255, 0.12)",
  };
  const typography = perlTypography || {
    family: "'Inter', sans-serif",
  };

  // Build product information from productData if available
  const buildProductInfo = () => {
    if (!productData) {
      // Fallback to topAssumptions if productData not available
      return assumptions && assumptions.length > 0 ? assumptions.map((assumption) => assumption) : [];
    }

    const productInfo = [];

    // Product Name
    if (productData.productName) {
      productInfo.push(`Product Name: ${productData.productName}`);
    }

    // Material
    if (productData.materialSelection) {
      productInfo.push(`Material: ${productData.materialSelection}`);
    }

    // Batch Size
    if (productData.batchSize) {
      productInfo.push(`Batch Size: ${productData.batchSize}`);
    }

    // Part Dimensions
    const dims = productData.partWeightDimensions;
    if (dims) {
      const dimParts = [];
      if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
      if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
      if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
      if (dimParts.length > 0) {
        productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
      }
    }

    // Part Details
    const details = productData.partDetails;
    if (details) {
      const detailParts = [];
      if (details.wall) detailParts.push(`Wall: ${details.wall}`);
      if (details.avgWallThickness)
        detailParts.push(`ECT: ${details.avgWallThickness}`);
      if (details.burstingFactor)
        detailParts.push(`Bursting Factor: ${details.burstingFactor}`);
      if (details.noOfPly) detailParts.push(`No of ply: ${details.noOfPly}`);
      if (details.flute) detailParts.push(`Flute: ${details.flute}`);
      if (details.printing) detailParts.push(`Printing: ${details.printing}`);
      if (details.numberOfColors) detailParts.push(`No of colors: ${details.numberOfColors}`);
      if (details.printingCoverage) detailParts.push(`Printing Coverage: ${details.printingCoverage}%`);
      if (details.inkCoverage) detailParts.push(`Ink coverage: ${details.inkCoverage}%`);
      if (detailParts.length > 0) {
        productInfo.push(`Part Details: ${detailParts.join(", ")}`);
      }
    }

    // Manufacturing Country
    if (productData.manufacturingCountry) {
      productInfo.push(
        `Manufacturing Country: ${productData.manufacturingCountry}`
      );
    }

    // Add remaining topAssumptions that aren't already covered
    if (assumptions && assumptions.length > 0) {
      const bottomSectionKeywords = [
        "Machine tonnage:",
        "Tonnage:",
        "Cycle time:",
        "Cycle Time(sec):",
        "Machine rate:",
        "OHP rate:",
        "No. of operations:",
        "Material price:",
        "Material Price:",
        "Labour cost:",
        "Labour Rate:",
        "Glue flap:",
        "Board cost per box:",
        "Ink cost per box:",
        "Material spec:",
        "Ink cost per_box:",
        "Total conversion cost:",
        "OHP:",
      ];

      const topAssumptions = assumptions.filter(
        (assumption) =>
          !bottomSectionKeywords.some((keyword) =>
            assumption.toLowerCase().startsWith(keyword.toLowerCase())
          )
      );

      const coveredKeywords = [
        "product name:",
        "material:",
        "batch size:",
        "part dimensions:",
        "part details:",
        "manufacturing country:",
        "board cost:",
        "ink cost:",
      ];
      const remainingAssumptions = topAssumptions.filter((assumption) => {
        const lowerAssumption = assumption.toLowerCase().trim();
        if (
          lowerAssumption.startsWith("board cost") ||
          lowerAssumption.startsWith("ink cost")
        ) {
          return false;
        }
        return !coveredKeywords.some((keyword) =>
          lowerAssumption.startsWith(keyword)
        );
      });

      return [...productInfo, ...remainingAssumptions];
    }

    return productInfo;
  };

  const productInfoItems = buildProductInfo();
  
  // Get bottom assumptions (machine parameters, rates, etc.)
  const bottomSectionKeywords = [
    "Machine tonnage:",
    "Tonnage:",
    "Cycle time:",
    "Cycle Time(sec):",
    "Machine rate:",
    "No. of operations:",
    "Material price:",
    "Material Price:",
    "Labour cost:",
    "Labour Rate:",
    "Glue flap:",
    "Board cost per box:",
    "Ink cost per box:",
    "Material spec:",
    "Ink cost per_box:",
    "Total conversion cost:",
    "OHP:",
  ];
  
  const bottomAssumptions = (assumptions || []).filter((assumption) => {
    const lowerAssumption = assumption.toLowerCase();
    // Exclude OHP rate from assumptions
    if (lowerAssumption.startsWith("ohp rate:") || lowerAssumption.startsWith("ohp:")) {
      return false;
    }
    return bottomSectionKeywords.some((keyword) =>
      lowerAssumption.startsWith(keyword.toLowerCase())
    );
  });
  
  // Add paper parameters from formulaResult to bottomAssumptions
  const paperParameters = [];
  if (formulaResult && formulaResult.paper) {
    const paper = formulaResult.paper;
    if (paper.top_liner_gsm !== undefined && paper.top_liner_gsm !== null) {
      paperParameters.push(`Top liner GSM: ${paper.top_liner_gsm}`);
    }
    if (paper.medium_gsm !== undefined && paper.medium_gsm !== null) {
      paperParameters.push(`Medium GSM: ${paper.medium_gsm}`);
    }
    if (paper.bottom_liner_gsm !== undefined && paper.bottom_liner_gsm !== null) {
      paperParameters.push(`Bottom liner GSM: ${paper.bottom_liner_gsm}`);
    }
    if (paper.total_gsm !== undefined && paper.total_gsm !== null) {
      paperParameters.push(`Total GSM: ${paper.total_gsm}`);
    }
    if (paper.take_up_factor !== undefined && paper.take_up_factor !== null) {
      paperParameters.push(`Take up factor: ${paper.take_up_factor}`);
    }
  }
  
  // Combine bottomAssumptions with paper parameters
  const allBottomAssumptions = [...bottomAssumptions, ...paperParameters];

  // Show section if we have product info or assumptions
  const hasContent = productInfoItems.length > 0 || allBottomAssumptions.length > 0;

  return (
    <Box>
      {hasContent && (
        <Paper
          sx={{
            p: 3,
            bgcolor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          {(() => {
            return (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: typography.family,
                      letterSpacing: "0.3px",
                      color: colors.textPrimary,
                    }}
                  >
                    Product Information
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {productInfoItems.map((item, idx) => (
                      <Typography
                        key={idx}
                        component="li"
                        variant="body2"
                        sx={{
                          mb: 1,
                          fontSize: "14px",
                          fontFamily: typography.family,
                          color: colors.textSecondary,
                          lineHeight: "20px",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {allBottomAssumptions.length > 0 && (
                  <Box
                    sx={{
                      width: "1px",
                      bgcolor: colors.border || "rgba(255, 255, 255, 0.12)",
                      alignSelf: "stretch",
                    }}
                  />
                )}

                {allBottomAssumptions.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        fontFamily: typography.family,
                        letterSpacing: "0.3px",
                        color: colors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {allBottomAssumptions.map((assumption, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: typography.family,
                            color: colors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {assumption}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>
      )}
    </Box>
  );
}

export default function CorrugatedPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
    textSecondary: "#A0A0A0",
    surface: "#242526",
    border: "rgba(255, 255, 255, 0.12)",
  };
  const perlTypography = theme.custom?.perlTypography || {
    family: "'Inter', sans-serif",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  // Helper function to recalculate OHP cost when OHP Rate changes
  const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
    if (!costData || !costData.breakdown || !costData._originalResult) {
      return costData;
    }

    const to4 = (n) => {
      if (n === null || n === undefined || n === '') return 0;
      if (typeof n === 'string') {
        const cleaned = n.replace(/[$,\s]/g, '').trim();
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
      }
      return Number(Number(n).toFixed(4));
    };

    // Get base material and process costs (these don't change)
    const material = Number(costData.breakdown.material || 0);
    const process = Number(costData.breakdown.process || 0);
    const baseOhpCost = Number(costData.meta?.baseOhpCost ?? costData.breakdown.ohp ?? 0);
    const baseOhpRate =
      Number(costData.meta?.baseOhpRate) ||
      (material + process > 0 ? (baseOhpCost / (material + process)) * 100 : 0);
    const baseFormulaCalc =
      material + process > 0 ? (material + process) * (baseOhpRate / 100) : 0;
    console.log(
      "🧮 Base OHP Formula:",
      `(${material} + ${process}) × (${baseOhpRate}/100) = ${baseFormulaCalc}`
    );

    // Recalculate OHP based on percentage change relative to original rate
    const targetOhpRate =
      typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate ?? 0);
    console.log(
      "➡️ Recalculating OHP:",
      `Current meta rate=${costData.meta?.currentOhpRate}, base rate=${baseOhpRate}`
    );

    let ohp = baseOhpCost;
    if (!isNaN(targetOhpRate)) {
      if (baseOhpRate > 0) {
        const rateRatio = targetOhpRate / baseOhpRate;
        const calc = baseOhpCost * rateRatio;
        console.log(
          "📐 OHP (based on ratio):",
          `(${baseOhpCost} * ${rateRatio}) = ${calc}`
        );
        ohp = to4(calc);
        } else {
        const ohpRateDecimal =
          targetOhpRate > 1 ? targetOhpRate / 100 : targetOhpRate;
        const calc = (material + process) * ohpRateDecimal;
        console.log(
          "📐 OHP (Material+Process * rate):",
          `(${material} + ${process}) * ${ohpRateDecimal} = ${calc}`
        );
        ohp = to4(calc);
      }
      const targetFormulaCalc =
        (material + process) * (targetOhpRate / 100);
      console.log(
        "🧮 Target OHP Formula:",
        `(${material} + ${process}) × (${targetOhpRate}/100) = ${targetFormulaCalc}`
      );
    }

    // Calculate new total: material + process + ohp
    const calculatedTotal = Number(material) + Number(process) + Number(ohp);
    const total = to4(calculatedTotal);

    // Update assumptions array with updated PlayBook values
    const resultData = costData._originalResult;
    const assumptionsArray = [];
    
    // Part Dimensions - from product data
    const dimensionData = productData?.partWeightDimensions || {};
    if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
      assumptionsArray.push(
        `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
      );
    }
    
    // Machine Tonnage
    if (updatedCostParams.machineTonnage) {
      assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
    } else if (resultData.Tonnage || resultData.cTonnage) {
      assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
    }
    
    // Cavities
    if (updatedCostParams.cavities) {
      assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
    } else if (resultData.cCavities || resultData.Cavities) {
      assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
    }
    
    // Cycle Time
    if (updatedCostParams.cycleTime) {
      assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
    } else if (resultData.CycleTime) {
      assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
    }
    
    // Machine Rate - always from stored procedure
    if (resultData.MHR) {
      const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
      if (!isNaN(machineRate)) {
        assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
      }
    }
    
    // Material Price - always from stored procedure
    if (resultData.RMPrice) {
      const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
      if (!isNaN(materialPrice)) {
        assumptionsArray.push(`Material price: $${materialPrice.toFixed(4)}/kg`);
      }
    }
    
    // Labour Cost - always from stored procedure
    if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
      const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number' 
        ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
        : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
      if (!isNaN(labourCost)) {
        assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
      }
    }
    
    // OHP Rate - use updated value
    if (newOhpRate !== null && newOhpRate !== undefined && newOhpRate !== 0) {
      const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
      if (!isNaN(ohpRateValue)) {
        assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
      }
    }

    // Update cost data with new OHP, total, and assumptions
    return {
      ...costData,
      breakdown: {
        ...costData.breakdown,
        ohp: ohp,
        total_should_cost: total,
      },
      summary: costData.summary.replace(
        /\$[\d.]+ per unit/,
        `$${total} per unit`
      ),
      assumptions:
        assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
      meta: {
        ...costData.meta,
        currentOhpRate: targetOhpRate,
      },
    };
  };

  // Helper function to fetch material price and update material cost when tonnage changes
  const updateMaterialCostFromTonnage = async (tonnage, updatedCostParams) => {
    console.log("\n" + "=".repeat(80));
    console.log("💰 updateMaterialCostFromTonnage called");
    console.log("=".repeat(80));
    console.log("📥 Input Parameters:");
    console.log("   - Tonnage:", tonnage);
    console.log("   - CostData exists:", !!costData);
    console.log("   - CostData source:", costData?._source);
    console.log("   - ProductData exists:", !!productData);

    if (!costData || costData._source !== "stored_procedure" || !productData) {
      console.log("⚠️ Early return: Missing costData or productData");
      return;
    }

    try {
      const manufacturingCountry = productData.manufacturingCountry || "";
      const materialName = productData.materialSelection || null;
      const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

      console.log("📋 Product Data:");
      console.log("   - Manufacturing Country:", manufacturingCountry);
      console.log("   - Material Name:", materialName);
      console.log("   - Part Weight (g):", partWeightG);

      if (!manufacturingCountry || !tonnage || partWeightG <= 0) {
        console.warn("⚠️ Cannot update material cost: missing country, tonnage, or part weight");
        console.log("   - Country:", manufacturingCountry || "MISSING");
        console.log("   - Tonnage:", tonnage || "MISSING");
        console.log("   - Part Weight:", partWeightG || "MISSING");
        return;
      }

      // Get current material cost before update
      const currentMaterialCost = costData.breakdown?.material || 0;
      const currentMaterialPricePerKg = costData.meta?.materialPricePerKg || null;
      
      console.log("\n📊 Current Values:");
      console.log("   - Current Material Cost:", `$${currentMaterialCost}/unit`);
      console.log("   - Current Material Price:", currentMaterialPricePerKg ? `$${currentMaterialPricePerKg}/kg` : "N/A");

      const requestPayload = {
        tonnage: tonnage,
        manufacturingCountry: manufacturingCountry,
        materialName: materialName,
      };

      console.log("\n📤 API Request Payload:");
      console.log(JSON.stringify(requestPayload, null, 2));
      console.log(`\n🔁 Fetching material price for ${tonnage} tons in ${manufacturingCountry}...`);

      const response = await apiFetch("/api/mech/material-price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      console.log("📥 API Response Status:", response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
        console.error("❌ API Error Response:", errorData);
        throw new Error(errorData.error || "Failed to fetch material price");
      }

      const result = await response.json();
      console.log("\n📥 API Response Data:");
      console.log(JSON.stringify(result, null, 2));

      const newMaterialPricePerKg = parseFloat(result.material_price || 0);
      console.log("\n💰 Material Price Parsed:");
      console.log("   - Raw value:", result.material_price);
      console.log("   - Parsed value:", newMaterialPricePerKg);

      if (isNaN(newMaterialPricePerKg) || newMaterialPricePerKg <= 0) {
        console.warn("⚠️ Invalid material price received:", result);
        return;
      }

      // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
      // const partWeightKg = parseFloat(partWeightG) / 1000;
      // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
      // Update Material in cost category table with material price per kg directly
      const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

      console.log("\n🧮 Calculation:");
      console.log("   - Material Price ($/kg):", newMaterialPricePerKg);
      console.log("   - Using material price per kg directly as material cost");
      console.log("   - New Material Cost ($/unit):", newMaterialCost);

      // Update costData with new material cost
      const to4 = (n) => {
        if (n === null || n === undefined || n === '') return 0;
        if (typeof n === 'string') {
          const cleaned = n.replace(/[$,\s]/g, '').trim();
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
        }
        return Number(Number(n).toFixed(4));
      };

      const process = to4(costData.breakdown.process || 0);
      const ohp = to4(costData.breakdown.ohp || 0);
      const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

      console.log("\n📊 Cost Breakdown Update:");
      console.log("   - Material (old):", `$${currentMaterialCost}/unit`);
      console.log("   - Material (new):", `$${newMaterialCost}/unit`);
      console.log("   - Process:", `$${process}/unit`);
      console.log("   - OHP:", `$${ohp}/unit`);
      console.log("   - Total (old):", `$${costData.breakdown.total_should_cost}/unit`);
      console.log("   - Total (new):", `$${newTotal}/unit`);
      console.log("   - Total Formula:", `${newMaterialCost} + ${process} + ${ohp} = ${newTotal}`);

      const updatedCostData = {
        ...costData,
        breakdown: {
          ...costData.breakdown,
          material: newMaterialCost,
          total_should_cost: newTotal,
        },
        summary: costData.summary.replace(
          /\$[\d.]+ per unit/,
          `$${newTotal} per unit`
        ),
        assumptions: costData.assumptions.map((assumption) => {
          // Update material price in assumptions if it exists
          if (assumption.includes("Material price:")) {
            return `Material price: $${newMaterialPricePerKg.toFixed(4)}/kg`;
          }
          return assumption;
        }),
        meta: {
          ...costData.meta,
          materialPricePerKg: newMaterialPricePerKg,
        },
      };

      setCostData(updatedCostData);
      console.log("\n✅ Material cost updated successfully!");
      console.log("   - New Material Price:", `$${newMaterialPricePerKg}/kg`);
      console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);
      console.log("   - New Total Cost:", `$${newTotal}/unit`);
      console.log("=".repeat(80) + "\n");
    } catch (error) {
      console.error("\n❌ Failed to update material cost:");
      console.error("   - Error type:", error.constructor.name);
      console.error("   - Error message:", error.message);
      console.error("   - Stack:", error.stack);
      console.log("=".repeat(80) + "\n");
      // Don't show alert to user, just log the error
    }
  };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previousOhpRate = playBookData?.costParams?.ohpRate;
      const newOhpRate = value.ohpRate;
      const previousTonnage = playBookData?.costParams?.machineTonnage;
      const newTonnage = value.machineTonnage;
      
      setPlayBookData({
        ...playBookData,
        costParams: value,
      });
      setKeyInsightsData(null);

      // If OHP Rate changed and we have existing costData, recalculate OHP cost
      if (costData && costData._source === "stored_procedure" && 
          newOhpRate !== undefined && newOhpRate !== null && 
          newOhpRate !== previousOhpRate) {
        const updatedCostData = recalculateOHPCost(newOhpRate, costData, value);
        setCostData(updatedCostData);
      }

      // For corrugated: Update OHP rate in assumptions when PlayBook changes
      if (costData && costData._source !== "stored_procedure" && 
          newOhpRate !== undefined && newOhpRate !== null && 
          newOhpRate !== previousOhpRate) {
        const updatedAssumptions = (costData.assumptions || []).map((assumption) => {
          // Replace "OHP: X%" or "OHP rate: X%" with the new value
          if (typeof assumption === 'string') {
            const lowerAssumption = assumption.toLowerCase();
            if (lowerAssumption.startsWith("ohp:") || lowerAssumption.startsWith("ohp rate:")) {
              const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
              if (!isNaN(ohpRateValue)) {
                return `OHP: ${ohpRateValue.toFixed(2)}%`;
              }
            }
          }
          return assumption;
        });
        
        // If OHP wasn't found in assumptions, add it
        const hasOhp = updatedAssumptions.some((a) => 
          typeof a === 'string' && (a.toLowerCase().startsWith("ohp:") || a.toLowerCase().startsWith("ohp rate:"))
        );
        if (!hasOhp && newOhpRate !== undefined && newOhpRate !== null && newOhpRate !== 0) {
          const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
          if (!isNaN(ohpRateValue)) {
            updatedAssumptions.push(`OHP: ${ohpRateValue.toFixed(2)}%`);
          }
        }

        setCostData({
          ...costData,
          assumptions: updatedAssumptions,
        });
      }

      // Note: Material price update is now handled when Quick Cost button is clicked
      // No automatic update here - wait for user to click Quick Cost button
    } else {
    setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      // Call all three APIs in parallel
      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/fnb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      // Parse all responses (non-blocking for risk and substitute)
      const costResult = await costResponse.json();
      console.log("✅ Cost analysis complete:", costResult);
      setCostData(costResult);

      // Handle risk analysis
      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        console.log("✅ Risk analysis complete:", riskResult);
        setRiskData(riskResult);
      } else {
        console.warn("⚠️ Risk analysis failed, continuing without risk data");
        setRiskData(null);
      }

      // Handle substitute analysis
      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        console.log("✅ Substitute analysis complete:", substituteResult);
        setSubstituteData(substituteResult);
      } else {
        console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
        setSubstituteData(null);
      }

      // Generate product image with the playbook data
      await generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook, costBreakdownOverride = null) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Packaging",
      costModel: costBreakdownOverride || costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [{ ingredient: product?.materialSelection || "Corrugated Board", percentage: 100, functional_group: "Base" }];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "corrugated packaging"} based on available costing inputs.`,
      top_concerns: [
        "Paper/board price volatility can impact unit cost.",
        "Print and conversion variability can increase scrap/rework.",
        "Supplier lead-time fluctuations can affect fulfillment reliability.",
      ],
      risk_items: [
        { title: "Board Cost Volatility", severity: 6, probability: 6, detail: "Track kraft/linerboard indices and contract terms." },
        { title: "Conversion Process Variability", severity: 5, probability: 5, detail: "Monitor cycle losses, print rejects, and yield." },
      ],
      assumptions: "Generated from corrugated product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const details = product?.partDetails || {};
      const material = product?.materialSelection || "Current board spec";
      const flute = details.flute || "Current flute";
      const ply = details.noOfPly || "Current ply";
      const printing = details.printing || "Current printing setup";

      return {
        summary: `Corrugated optimization recommendations generated for ${product?.productName || "the current packaging"} using provided inputs.`,
        substitutes: [
          {
            current_item: `Board spec: ${material}, Flute: ${flute}, Ply: ${ply}`,
            proposed_item: "Validate fit-for-purpose board grade optimization",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires compression/transport validation",
            feasibility_score: 6,
            timeline_weeks: 3,
            risk_note: "Validate stacking strength and transit performance.",
            validation_steps: ["Spec review", "Burst/ECT test", "Transit simulation"],
          },
          {
            current_item: `Printing setup: ${printing}`,
            proposed_item: "Optimize print coverage and ink consumption",
            estimated_cost_delta_per_unit: "Potential consumables cost reduction",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 2,
            risk_note: "Verify color consistency and brand requirements.",
            validation_steps: ["Print trial", "Color check", "Reject-rate tracking"],
          },
        ],
        prioritization: [
          "Print/ink optimization for immediate savings",
          "Board grade optimization after validation",
        ],
        assumptions: "Generated from corrugated product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!riskResponse.ok || !substituteResponse.ok) {
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        setRiskData(await riskResponse.json());
      } else {
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        setSubstituteData(await substituteResponse.json());
      } else {
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const imagePayload = {
        productName: product.productName,
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Corrugated Box",
        primaryPackaging: playbook?.primary_packaging || "",
        skuWt: product.skuWeight || "",
        skuWtUnit: product.skuWeightUnit || "g",
        marketPositioning: product.marketPositioning || "",
        style: "professional",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      const response = await apiFetch("/api/pkg/corrugated/generate-product-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("✅ Product image generated:", data.image_url);
    } catch (error) {
      console.error("❌ Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      return;
    }
    try {
      const response = await apiFetch("/api/mech/key-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate key insights");
      }
      const insights = await response.json();
      setKeyInsightsData(insights);
    } catch (error) {
      console.error("Failed to fetch key insights:", error);
    }
  };

  // Progressive Quick Cost with SSE streaming
  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis with SSE...");

    // Initialize progress steps immediately
    const totalSteps = 6;
    const initialSteps = Array.from({ length: totalSteps }, (_, i) => ({
      id: `step-${i + 1}`,
      label: i === 0 ? "Processing..." : `Step ${i + 1}`,
      status: "pending",
      message: "",
    }));
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);

    // Track maximum progress to prevent going backwards
    let maxProgress = 0;

    try {
      const timestamp = Date.now();

      // Prepare request payload matching backend expectations
      const requestData = {
        productData: productData,
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        // Include playbook if available
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/packaging/corrugated/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📥 SSE Event:", eventData);

            if (eventData.type === "start") {
              // Update total steps if different from initial estimate
              const totalSteps = eventData.total_steps || 6;
              if (totalSteps !== progressSteps.length) {
                const updatedSteps = Array.from({ length: totalSteps }, (_, i) => ({
                  id: `step-${i + 1}`,
                  label: `Step ${i + 1}`,
                  status: "pending",
                  message: "",
                }));
                setProgressSteps(updatedSteps);
              }
            } else if (eventData.type === "progress") {
              // Update specific step status
              const stepIndex = eventData.step - 1;
              console.log(
                `📈 Progress Event - Step ${eventData.step}: ${eventData.label} (${eventData.status})`
              );

              setProgressSteps((prev) => {
                const updated = [...prev];
                if (updated[stepIndex]) {
                  // Map backend status to frontend status
                  let mappedStatus = eventData.status;
                  if (eventData.status === "running") {
                    mappedStatus = "in-progress";
                  } else if (eventData.status === "success") {
                    mappedStatus = "completed";
                  }

                  updated[stepIndex] = {
                    ...updated[stepIndex],
                    label: eventData.label || updated[stepIndex].label,
                    status: mappedStatus,
                    message:
                      eventData.message ||
                      (eventData.status === "running" ? "Processing..." : "Complete"),
                    error: eventData.error,
                  };

                  console.log(
                    `   ✓ Updated step ${eventData.step}: label="${updated[stepIndex].label}", status="${updated[stepIndex].status}"`
                  );
                }
                return updated;
              });

              // Update current step based on the most recent event
              if (eventData.status === "running" || eventData.status === "success") {
                setCurrentProgressStep(eventData.step);
              }

              const newProgress = (eventData.step / eventData.total) * 100;
              if (newProgress > maxProgress) {
                maxProgress = newProgress;
                setOverallProgress(newProgress);
              }
            } else if (eventData.type === "complete") {
              // Analysis complete - populate all state from final response
              console.log("✅ Analysis complete:", eventData);

              // Set progress to 100%
              maxProgress = 100;
              setOverallProgress(100);

              // Mark all steps as completed
              setProgressSteps((prev) =>
                prev.map((step) => ({
                  ...step,
                  status: "completed",
                  message: "Complete",
                }))
              );

              // Handle the response - same logic as before
              await handleStreamingComplete(eventData);

              // Close dialog and navigate
              setShowProgressTracker(false);
              setIsProcessing(false);
              setActiveSection("results");
              setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

              break;
            } else if (eventData.type === "error") {
              throw new Error(eventData.message || "Analysis failed");
            }
          } catch (parseErr) {
            console.warn("Failed to parse SSE event:", parseErr);
          }
        }
      }

      console.log("✅ Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("❌ Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  // Helper function to handle streaming complete event
  const handleStreamingComplete = async (costResult) => {
    if (costResult && costResult.status === 'success') {
      // Extract cost data from response
      let assumptionsArray = [];
      
      // Extract assumptions from response (could be array or object with sections)
      if (costResult.assumptions) {
        if (Array.isArray(costResult.assumptions)) {
          assumptionsArray = costResult.assumptions;
        } else if (typeof costResult.assumptions === 'object') {
          // Flatten assumptions object into array
          Object.values(costResult.assumptions).forEach((section) => {
            if (Array.isArray(section)) {
              assumptionsArray.push(...section);
            }
          });
        }
      }
      
      // Build assumptions from product data and formula result if not available
      if (assumptionsArray.length === 0 && productData) {
        // Build assumptions from product data
        if (productData.productName) {
          assumptionsArray.push(`Product Name: ${productData.productName}`);
        }
        if (productData.materialSelection) {
          assumptionsArray.push(`Material: ${productData.materialSelection}`);
        }
        if (productData.batchSize) {
          assumptionsArray.push(`Batch Size: ${productData.batchSize}`);
        }
        
        // Part Dimensions
        const dims = productData.partWeightDimensions;
        if (dims) {
          const dimParts = [];
          if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
          if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
          if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
          if (dimParts.length > 0) {
            assumptionsArray.push(`Part Dimensions: ${dimParts.join(", ")}`);
          }
        }
        
        // Part Details
        const details = productData.partDetails;
        if (details) {
          const detailParts = [];
          if (details.wall) detailParts.push(`Wall: ${details.wall}`);
          if (details.avgWallThickness) detailParts.push(`ECT: ${details.avgWallThickness}`);
          if (details.burstingFactor) detailParts.push(`Bursting Factor: ${details.burstingFactor}`);
          if (details.noOfPly) detailParts.push(`No of ply: ${details.noOfPly}`);
          if (details.flute) detailParts.push(`Flute: ${details.flute}`);
          if (details.printing) detailParts.push(`Printing: ${details.printing}`);
          if (details.numberOfColors) detailParts.push(`No of colors: ${details.numberOfColors}`);
          if (details.printingCoverage) detailParts.push(`Printing Coverage: ${details.printingCoverage}%`);
          if (details.inkCoverage) detailParts.push(`Ink coverage: ${details.inkCoverage}%`);
          if (detailParts.length > 0) {
            assumptionsArray.push(`Part Details: ${detailParts.join(", ")}`);
          }
        }
        
        // Manufacturing Country
        if (productData.manufacturingCountry) {
          assumptionsArray.push(`Manufacturing Country: ${productData.manufacturingCountry}`);
        }
        
        // Extract assumptions from formula result if available
        if (costResult.formulaResult) {
          const formula = costResult.formulaResult;
          
          // Paper parameters
          if (formula.paper) {
            const paper = formula.paper;
            if (paper.paper_price_per_kg_usd) {
              assumptionsArray.push(`Paper price: $${paper.paper_price_per_kg_usd.toFixed(4)}/kg`);
            }
          }
          
          // Machine costs
          if (formula.machines) {
            const machines = formula.machines;
            if (machines.conversion_cost) {
              assumptionsArray.push(`Total conversion cost: $${machines.conversion_cost.toFixed(4)}`);
            }
          }
        }
      }
      
      if (costResult.ShouldCostResult) {
        const shouldCost = costResult.ShouldCostResult;
        const formulaResult = costResult.formulaResult || null;
        
        setCostData({
          breakdown: {
            material: shouldCost.MaterialCost || 0,
            process: shouldCost.ProcessCost || 0,
            packaging: shouldCost.PackagingCost || 0,
            ohp: shouldCost.OHPCost || 0,
          },
          total: shouldCost.TotalCost || 0,
          assumptions: assumptionsArray,
          _source: "corrugated_streaming",
          _originalResult: costResult,
          formulaResult: formulaResult,
        });

        // Calculate OHP percentage from cost breakdown or use from formulaResult
        const material = shouldCost.MaterialCost || 0;
        const process = shouldCost.ProcessCost || 0;
        const ohp = shouldCost.OHPCost || 0;
        const baseCost = material + process;
        
        // Try to get OHP percentage from formulaResult first, otherwise calculate from cost breakdown
        let ohpRatePercent = 0;
        if (formulaResult && formulaResult.overheads_and_profits && formulaResult.overheads_and_profits.overheads_and_profits_percent) {
          ohpRatePercent = formulaResult.overheads_and_profits.overheads_and_profits_percent;
        } else if (baseCost > 0) {
          // Calculate as percentage of (Material + Process)
          ohpRatePercent = (ohp / baseCost) * 100;
        }

        // Map formulaResult geometry values to playBookData
        if (formulaResult && formulaResult.geometry) {
          const geometry = formulaResult.geometry;
          const blankLengthMm = geometry.blank_length_mm || 0;
          const blankWidthMm = geometry.blank_width_mm || 0;
          const sheetAreaM2 = geometry.sheet_area_m2 || 0;

          // Convert mm to cm for Playbook component (it stores in cm but displays in mm)
          const blankLengthCm = blankLengthMm / 10;
          const blankWidthCm = blankWidthMm / 10;

          // Get existing playBookData or initialize
          const existingPlayBookData = playBookData || {};
          const existingCostParams = existingPlayBookData.costParams || {};

          // Update playBookData with geometry values and OHP rate from formulaResult
          setPlayBookData({
            ...existingPlayBookData,
            costParams: {
              ...existingCostParams,
              blankLengthCm: blankLengthCm,
              blankWidthCm: blankWidthCm,
              areaNetM2: sheetAreaM2,
              ohpRate: parseFloat(ohpRatePercent.toFixed(2)),
            },
          });
        } else {
          // If no geometry data, still update OHP rate if we have cost data
          if (ohpRatePercent > 0) {
            const existingPlayBookData = playBookData || {};
            const existingCostParams = existingPlayBookData.costParams || {};
            
            setPlayBookData({
              ...existingPlayBookData,
              costParams: {
                ...existingCostParams,
                ohpRate: parseFloat(ohpRatePercent.toFixed(2)),
              },
            });
          }
        }
      }

      // Handle playbook data if returned
      if (costResult.playBookData) {
        const unwrappedPlayBookData = costResult.playBookData.playBookData || costResult.playBookData;
        // Merge with existing playBookData to preserve geometry values if already set
        setPlayBookData((prev) => ({
          ...prev,
          ...unwrappedPlayBookData,
          costParams: {
            ...(prev?.costParams || {}),
            ...(unwrappedPlayBookData?.costParams || {}),
          },
        }));
      }

      // Handle other data (risk, substitute, image, etc.)
      if (costResult.riskData) {
        const unwrappedRiskData = costResult.riskData.riskData || costResult.riskData;
        setRiskData(unwrappedRiskData);
      }

      if (costResult.substituteData) {
        const unwrappedSubstituteData = costResult.substituteData.substituteData || costResult.substituteData;
        setSubstituteData(unwrappedSubstituteData);
      }

      if (!costResult.riskData || !costResult.substituteData) {
        await fetchRiskAndSubstituteData(
          productData,
          playBookData || {},
          {
            material: costResult?.ShouldCostResult?.MaterialCost || 0,
            process: costResult?.ShouldCostResult?.ProcessCost || 0,
            ohp: costResult?.ShouldCostResult?.OHPCost || 0,
          }
        );
      }

      if (costResult.productImage && costResult.productImage.imageUrl) {
        setProductImageUrl(costResult.productImage.imageUrl);
      }

      await fetchKeyInsights(productData, playBookData || {});
      // Trigger image generation after Quick Cost completes (non-blocking)
      generateProductImageWithData(productData, playBookData || {});
    } else {
      throw new Error("Corrugated analysis returned no data");
    }
  };

  const handleQuickCost = async () => {
    const Ai_Maintenance = "Schema Management V2";

    if (Ai_Maintenance === "Schema Management V2") {
      handleProgressiveQuickCost();
      return;
    }
    // Legacy non-progressive path
    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);
    setKeyInsightsData(null);

    try {
      const timestamp = Date.now();

      // Prepare request payload
      const requestData = {
        productData: productData,
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling legacy endpoint:", requestData);

      // Call SSE streaming endpoint (without progress tracking)
      const response = await apiFetch("/api/packaging/corrugated/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream (simplified - just wait for complete event)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let costResult = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const event of events) {
          if (!event.trim()) continue;

          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);

            if (eventData.type === "complete") {
              costResult = eventData;
              break;
            } else if (eventData.type === "error") {
              throw new Error(eventData.message || "Analysis failed");
            }
          } catch (parseErr) {
            console.warn("Failed to parse SSE event:", parseErr);
          }
        }

        if (costResult) break;
      }

      if (!costResult) {
        throw new Error("Stream ended without complete event");
      }

      // Handle the response
      await handleStreamingComplete(costResult);

      // Navigate to results section
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to calculate cost: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    // Calculate total as sum of all visible rows to ensure it matches what's displayed
    const sumOfRows = costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
    
    // Use the calculated sum instead of the declared total to ensure accuracy
    // The declared total from backend may include items not shown in breakdown (e.g., labour)
    console.log("💰 Cost Total Calculation:", {
      sumOfRows,
      costRows: costRows.map(r => ({ key: r.key, value: r.value })),
      declaredTotal: costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total
    });
    
    return Number(sumOfRows.toFixed(4));
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Packaging"
          industry="Packaging"
          commodity="Corrugated"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height:"92%"}}>
                <CorrugatedQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {activeSection === "playbook" && (
              <Box>
                <CorrugatedPlayBook 
                  data={playBookData || { costParams: {} }} 
                  onChange={handlePlayBookChange} 
                />
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    substituteSectionTitle="Corrugated Recommendations"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: costData?.assumptions || [],
                      productData,
                      perlColors,
                      perlTypography,
                      formulaResult: costData?.formulaResult || null,
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}
        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
